import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { HashOperation } from "./HashOperation";
import { URLOperation } from "./URLOperation";
import { ErrorOperation } from "./ErrorOperation";
import { ParsedNameValue } from "./ParsedNameValue";
import { ConsoleOperation } from "./ConsoleOperation";
import { EnvService } from "./EnvService";

export abstract class BaseServer
{
    protected base_url: string;
    private onBeforeRequests: ((url: string, config?: AxiosRequestConfig) => void)[] = [];
    private onAfterRequests: ((url: string, res: AxiosResponse) => void)[] = [];
    protected abstract onError(error: Error): void;
    public suppressErrors: boolean = false;
    public suppressOnErrors: boolean = false;
    public verboseOnErrors: boolean;

    constructor(base_url: string)
    {
        this.base_url = base_url;

        this.verboseOnErrors = new EnvService("BASESERVER_ERROR_VERBOSE", false).getBoolean(false);
    }
    public addOnBeforeRequest(onBeforeRequest: (url: string, config?: AxiosRequestConfig) => void)
    {
        this.onBeforeRequests.push(onBeforeRequest);
    }
    public addOnAfterRequest(onAfterRequest: (url: string, res: AxiosResponse) => void)
    {
        this.onAfterRequests.push(onAfterRequest);
    }
    private async _request<ResData = any, ReqData = any>(onRequest: (url: string, data?: any, config?: AxiosRequestConfig<ReqData>) => Promise<AxiosResponse<ResData>>, sub: string, query?: { [name: string]: ParsedNameValue }, data?: ReqData, config?: AxiosRequestConfig<ReqData>, sign_header?: string, sign_key?: string): Promise<{ response: AxiosResponse<ResData>, data: ResData }>
    {
        let url: string = URLOperation.getLink(this.base_url, sub, query);
        if (!config)
            config = { headers: {} };
        if (sign_header)
            if (sign_key)
                if (data)
                    if (config?.headers)
                        config.headers[sign_header] = HashOperation.SHA256Secret(sign_key, data);
        try
        {
            this.onBeforeRequests.forEach(onBeforeRequest =>
            {
                onBeforeRequest(url, config);
            });
            let response: AxiosResponse<ResData> = await onRequest(url, data, config);
            this.onAfterRequests.forEach(onAfterRequest =>
            {
                onAfterRequest(url, response);
            });
            return { response, data: response.data };
        } catch (error)
        {
            if (error instanceof Error)
            {
                if (this.onError)
                {
                    if (this.verboseOnErrors)
                        error.message = `Error calling url '${url}' ` + error.message;
                    if (!this.suppressOnErrors)
                        this.onError(error);
                }
                else
                {
                    ConsoleOperation.warning("onError function has not been properly set.");
                    ConsoleOperation.error(error?.message);
                }
                if (axios.isAxiosError(error))
                    if (error?.response?.data)
                        if (!this.suppressErrors)
                        {
                            if (this.verboseOnErrors)
                                ErrorOperation.throwHTTP(error.response.status, `Error calling url '${url}' ` + JSON.stringify(error.response.data));
                            else
                                ErrorOperation.throwHTTP(error.response.status, JSON.stringify(error.response.data));
                        }
            }
            if (!this.suppressErrors)
                throw error;
        }
        return {} as any;
    }
    public async _get<ResData = any, ReqData = any>(sub: string, query?: { [name: string]: ParsedNameValue }, config?: AxiosRequestConfig<ReqData>): Promise<{ response: AxiosResponse<ResData>, data: ResData }>
    {
        return await this._request(async (url: string, _?: any, config?: AxiosRequestConfig<ReqData>) =>
        {
            return await axios.get(url, config);
        }, sub, query, undefined, config);
    }
    public async _post<ResData = any, ReqData = any>(sub: string, query?: { [name: string]: ParsedNameValue }, data?: ReqData, config?: AxiosRequestConfig<ReqData>, sign_header?: string, sign_key?: string): Promise<{ response: AxiosResponse<ResData>, data: ResData }>
    {
        return await this._request(async (url: string, data: ReqData, config?: AxiosRequestConfig<ReqData>) =>
        {
            return await axios.post(url, data, config);
        }, sub, query, data, config, sign_header, sign_key);
    }
    public async _put<ResData = any, ReqData = any>(sub: string, query?: { [name: string]: ParsedNameValue }, data?: ReqData, config?: AxiosRequestConfig<ReqData>, sign_header?: string, sign_key?: string): Promise<{ response: AxiosResponse<ResData>, data: ResData }>
    {
        return await this._request(async (url: string, data: ReqData, config?: AxiosRequestConfig<ReqData>) =>
        {
            return await axios.put(url, data, config);
        }, sub, query, data, config, sign_header, sign_key);
    }
    public async _delete<ResData = any, ReqData = any>(sub: string, query?: { [name: string]: ParsedNameValue }, config?: AxiosRequestConfig<ReqData>, sign_header?: string, sign_key?: string): Promise<{ response: AxiosResponse<ResData>, data: ResData }>
    {
        return await this._request(async (url: string, _?: any, config?: AxiosRequestConfig<ReqData>) =>
        {
            return await axios.delete(url, config);
        }, sub, query, undefined, config, sign_header, sign_key);
    }
}